import React, { useContext, useState } from 'react';
import { get } from 'lodash';

import { Alert } from '@/RestoreComponent/Alert';
import { useGetShiftLeads } from '@/api/users/staff-management';
import { StaffShiftLeadResponseItem } from '@/api/users/users-api.schemas';
import { getNowEpoch } from '@/common/utils/time';

export const ShiftLeadAlertContext = React.createContext<{
  checkForShiftLead: () => void;
} | null>(null);

export type ShiftLeadAlertProviderProps = { children: React.ReactNode };

export const ShiftLeadAlertProvider: React.FC<ShiftLeadAlertProviderProps> = ({
  children
}) => {
  const [doCheck, setDoCheck] = useState(false);
  return (
    <ShiftLeadAlertContext.Provider
      value={{
        checkForShiftLead: () => {
          setDoCheck(true);
        }
      }}
    >
      {children}
      <ShiftLeadStatusAlert doCheck={doCheck} />
    </ShiftLeadAlertContext.Provider>
  );
};

const ShiftLeadStatusAlert = ({ doCheck }: { doCheck: boolean }) => {
  const [dismissed, setDismissed] = useState(false);

  const { data, isSuccess } = useGetShiftLeads({
    query: { enabled: doCheck }
  });

  const shiftLeads: StaffShiftLeadResponseItem[] = get(
    data,
    'data.shiftLeads',
    []
  );

  // A lead counts as on duty when now falls inside its shift. Half-open so
  // back-to-back shifts do not both match at the handoff minute.
  const epoch = getNowEpoch();
  const now = epoch ? Number(epoch.valueOf()) : 0;
  const hasShiftLead = shiftLeads.some(
    sl => sl.startDate <= now && now < sl.endDate
  );

  return (
    <Alert
      open={doCheck && isSuccess && !hasShiftLead && !dismissed}
      variant="warning"
      title="No Shift Lead Assigned"
      messages={[
        'Please inform your manager there is no shift Lead assigned.'
      ]}
      onClose={() => setDismissed(true)}
    />
  );
};

export const useShiftLeadAlert = () => useContext(ShiftLeadAlertContext);
